import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { UserRound, KeyRound, Save, Lock, Camera, Car, Flag, Timer, HardHat, Bell } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import InstagramButton from "@/components/InstagramButton";
import LogoImage from "@/components/LogoImage";
import PushOptIn from "@/components/PushOptIn";

const PRESETS = [
  { key: "casco", label: "Casco Racing", icon: HardHat, color: "#FF1E27" },
  { key: "auto", label: "Auto Prototipo", icon: Car, color: "#3B82F6" },
  { key: "volante", label: "Volante", icon: Timer, color: "#FFB703" },
  { key: "bandiera", label: "Bandiera a Scacchi", icon: Flag, color: "#22C55E" },
];

export function AvatarView({ user, size = 80 }) {
  if (user?.avatar) {
    return (
      <LogoImage
        src={user.avatar}
        fallbackSrc="/assets/avatar-fallback.svg"
        alt="Avatar"
        className="rounded-full object-cover border-2 border-[#FFB703]"
        style={{ width: size, height: size }}
        data-testid="avatar-image"
      />
    );
  }
  const preset = PRESETS.find((p) => p.key === user?.avatar_preset);
  const Icon = preset?.icon || UserRound;
  return (
    <div
      data-testid="avatar-preset"
      className="rounded-full flex items-center justify-center border-2"
      style={{
        width: size,
        height: size,
        borderColor: preset?.color || "#3F3F4C",
        background: `${preset?.color || "#3F3F4C"}22`,
      }}
    >
      <Icon className="w-1/2 h-1/2" style={{ color: preset?.color || "#9CA3AF" }} />
    </div>
  );
}

export default function Profile() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ username: user?.username || "", team_name: user?.team_name || "", email: user?.email || "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });

  const { data: catalog = [] } = useQuery({
    queryKey: ["badges-catalog"],
    queryFn: async () => (await api.get("/badges/catalog")).data,
  });

  const { data: settings } = useQuery({
    queryKey: ["settings-public"],
    queryFn: async () => (await api.get("/settings/public")).data,
  });
  const showTrophy = settings?.branding?.show_trophy_wall !== false;
  const showInsta = settings?.branding?.show_instagram_button !== false;

  const earned = Object.fromEntries((user?.badges || []).map((b) => [b.key, b]));

  const inputCls =
    "w-full rounded-lg bg-[#121214] border border-[#3F3F4C] px-3 py-2.5 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-[#FF1E27] transition-colors font-body-sport";

  const saveProfile = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put("/profile", form);
      setUser(data);
      toast.success("Profilo aggiornato in tutte le viste");
    } catch (e2) {
      toast.error(errMsg(e2, "Salvataggio fallito"));
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    try {
      await api.put("/profile/password", pw);
      toast.success("Password aggiornata");
      setPw({ current_password: "", new_password: "" });
    } catch (e2) {
      toast.error(errMsg(e2, "Cambio password fallito"));
    }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 800000) {
      toast.error("Immagine troppo grande (max 800KB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { data } = await api.put("/profile", { avatar: reader.result });
        setUser(data);
        toast.success("Foto profilo aggiornata");
      } catch (e2) {
        toast.error(errMsg(e2, "Upload fallito"));
      }
    };
    reader.readAsDataURL(file);
  };

  const pickPreset = async (key) => {
    try {
      const { data } = await api.put("/profile", { avatar: "", avatar_preset: key });
      setUser(data);
      toast.success("Avatar aggiornato");
    } catch (e) {
      toast.error(errMsg(e, "Errore"));
    }
  };

  return (
    <div data-testid="profile-page" className="space-y-6 max-w-5xl">
      <h2 className="font-racing font-bold text-xl sm:text-2xl uppercase tracking-wide text-white border-l-4 border-[#FF1E27] pl-3">
        Il Mio Profilo
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-[#3F3F4C] bg-[#2A2A32] p-5 space-y-4">
          <h3 className="font-body-sport font-bold text-white text-sm uppercase tracking-widest flex items-center gap-2">
            <Camera className="w-4 h-4 text-[#FFB703]" /> Avatar / Foto Profilo
          </h3>
          <div className="flex items-center gap-5">
            <AvatarView user={user} />
            <div className="space-y-2">
              <input
                type="file"
                accept="image/jpeg,image/png"
                data-testid="avatar-upload-input"
                onChange={uploadAvatar}
                className="block w-full text-[11px] text-gray-400 file:mr-2 file:rounded-lg file:border-0 file:bg-[#FF1E27] file:px-3 file:py-1.5 file:text-[10px] file:font-bold file:text-white file:uppercase file:tracking-widest hover:file:bg-[#CC1118] file:cursor-pointer"
              />
              <p className="text-[10px] text-gray-500">JPEG/PNG, max 800KB — mostrata con ritaglio circolare</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                data-testid={`avatar-preset-${p.key}`}
                onClick={() => pickPreset(p.key)}
                title={p.label}
                className={`flex flex-col items-center gap-1 rounded-xl border px-3 py-2 min-h-[48px] transition-colors ${
                  user?.avatar_preset === p.key && !user?.avatar
                    ? "border-[#FFB703] bg-[#FFB703]/10"
                    : "border-[#3F3F4C] hover:border-[#FF1E27]"
                }`}
              >
                <p.icon className="w-6 h-6" style={{ color: p.color }} />
                <span className="font-body-sport text-[8px] tracking-widest text-gray-400 uppercase">{p.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-[#3F3F4C] bg-[#2A2A32] p-5 space-y-4">
          <h3 className="font-body-sport font-bold text-white text-sm uppercase tracking-widest flex items-center gap-2">
            <UserRound className="w-4 h-4 text-[#FF1E27]" /> Dati Personali
          </h3>
          <form onSubmit={saveProfile} className="space-y-3">
            <input data-testid="profile-username-input" className={inputCls} placeholder="Nome Utente" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
            <input data-testid="profile-team-input" className={inputCls} placeholder="Nome Scuderia (anche caratteri speciali)" value={form.team_name} onChange={(e) => setForm({ ...form, team_name: e.target.value })} />
            <input data-testid="profile-email-input" type="email" className={inputCls} placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <button data-testid="profile-save-button" className="flex items-center gap-2 rounded-lg bg-[#FF1E27] hover:bg-[#CC1118] transition-colors px-5 py-2.5 min-h-[44px] font-body-sport font-bold text-xs tracking-widest text-white uppercase">
              <Save className="w-4 h-4" /> Salva Dati
            </button>
          </form>
          <form onSubmit={changePassword} className="space-y-3 pt-3 border-t border-[#3F3F4C]">
            <h4 className="font-body-sport font-bold text-gray-400 text-[10px] uppercase tracking-widest flex items-center gap-2">
              <KeyRound className="w-3.5 h-3.5" /> Cambia Password
            </h4>
            <input data-testid="profile-current-password" type="password" className={inputCls} placeholder="Password attuale" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} required />
            <input data-testid="profile-new-password" type="password" className={inputCls} placeholder="Nuova password (min 6 caratteri)" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} required />
            <button data-testid="profile-password-button" className="flex items-center gap-2 rounded-lg border border-[#FFB703] text-[#FFB703] hover:bg-[#FFB703]/10 transition-colors px-5 py-2.5 min-h-[44px] font-body-sport font-bold text-xs tracking-widest uppercase">
              Aggiorna Password
            </button>
          </form>
        </div>
      </div>

      <div className="rounded-xl border border-[#3F3F4C] bg-[#2A2A32] p-5 space-y-3">
        <h3 className="font-body-sport font-bold text-white text-sm uppercase tracking-widest flex items-center gap-2">
          <Bell className="w-4 h-4 text-[#FFB703]" /> Notifiche Push
        </h3>
        <p className="text-xs text-gray-400 font-body-sport">
          Ricevi un avviso su questo dispositivo quando la Direzione Gara pubblica i nuovi punteggi.
        </p>
        <PushOptIn />
      </div>

      {showTrophy && (
      <div className="rounded-xl border border-[#3F3F4C] bg-[#2A2A32] p-5 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-body-sport font-bold text-white text-sm uppercase tracking-widest">
            Bacheca Trofei ({(user?.badges || []).length}/{catalog.length})
          </h3>
          {showInsta && <InstagramButton />}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="trophy-grid">
          {catalog.map((b) => {
            const got = earned[b.key];
            return (
              <div
                key={b.key}
                data-testid={`trophy-${b.key}`}
                className={`rounded-xl border p-4 transition-colors ${
                  got
                    ? "border-[#FFB703]/50 bg-[#FFB703]/5"
                    : "border-[#3F3F4C] bg-[#121214] opacity-60"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className={`text-2xl ${got ? "" : "grayscale"}`}>{got ? b.icona : <Lock className="w-5 h-5 text-gray-600" />}</span>
                  {got?.unlocked_at && (
                    <span className="font-time text-[9px] text-gray-500">
                      {new Date(got.unlocked_at).toLocaleDateString("it-IT")}
                    </span>
                  )}
                </div>
                <p className={`font-body-sport font-bold text-sm mt-2 ${got ? "text-white" : "text-gray-500"}`}>
                  {b.nome}
                </p>
                <p className="text-[11px] text-gray-500 mt-1 leading-snug">{b.descrizione}</p>
              </div>
            );
          })}
        </div>
      </div>
      )}
    </div>
  );
}
